import { Box, Skeleton, type Theme, Typography } from "@mui/material";
import type { SystemStyleObject } from "@mui/system";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { SystemChipItem } from "../../../components/UIToolkit/Clickable/TabsAndChips/SystemChips/SystemChips";
import { EyebrowLabel } from "../../../components/UIToolkit/molecules/EyebrowLabel";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { useAuth } from "../../../contexts/AuthProvider";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useMyJobNotifications } from "../../../hooks/useMyJobNotifications";
import { CONTROL_HEIGHT, FONT_SIZE, INK } from "../../../theme/tokens";
import type { MyFacilityJobNotifications } from "../../../utils/jobNotificationTypes";
import { FacilityJobChips } from "./FacilityJobChips";

type Props = {
	/** The screen's card style, so this section matches the channel card above. */
	cardSx: SystemStyleObject<Theme>;
};

const FACILITY_NAME_SEPARATOR = " · ";

/**
 * Bedside "Which shifts" section: one chip per job, ticked while it notifies. A tap saves right away.
 * An employee with records at several facilities sees one group per facility, and a tap changes only that facility.
 * Hidden when the employee's only job notifies, since there is no choice. Shown if that job is muted, so they can turn it back on.
 */
export const WhichShiftsJobChips: FC<Props> = ({ cardSx }) => {
	const { t } = useTranslation();
	const { userInfo } = useAuth();
	const { track } = useAnalytics();
	const { showSnackbar } = useSnackbar();
	const { facilities, isLoading, isError, refetch, isRefetching, setJobSilent, saveErrors } = useMyJobNotifications();
	// DEFAULT: `facilities` is missing while loading or after an error. Both states render below.
	const facilityList = useMemo<MyFacilityJobNotifications[]>(() => facilities ?? [], [facilities]);
	const isMultiFacility = facilityList.length > 1;

	// While loading, only reserve space when the profile shows more than one job.
	// DEFAULT: `userInfo` is `{}` until it loads, and a nurse row may lack `jobIds`. Both mean no placeholder.
	const likelyHasChoice = (userInfo.nurses_info ?? []).reduce((count, info) => count + (info.nurse.jobIds ?? []).length, 0) > 1;
	if (isLoading)
		return likelyHasChoice ? <Skeleton variant="rounded" sx={{ mx: 2, mt: 2, height: CONTROL_HEIGHT.XL }} data-testid="which-shifts-loading" /> : null;
	const allJobs = facilityList.flatMap((facility) => facility.jobs);
	const hasChoice = allJobs.length > 1 || allJobs.some((job) => job.isSilent);
	if (!isError && !hasChoice) return null;

	const facilityName = (facility: MyFacilityJobNotifications) => facility.departmentNames.join(FACILITY_NAME_SEPARATOR);
	// With several facilities the job name alone doesn't say where the change applies.
	const jobLabel = (facility: MyFacilityJobNotifications, jobName: string) =>
		isMultiFacility ? `${jobName}${FACILITY_NAME_SEPARATOR}${facilityName(facility)}` : jobName;

	const handleChange = async (facility: MyFacilityJobNotifications, item: SystemChipItem<string>, selected: boolean) => {
		track("bedside_job_notification_toggle", { nurse_id: facility.nurseId, job_id: item.value, is_silent: !selected });
		try {
			await setJobSilent({ nurseId: facility.nurseId, jobId: item.value, isSilent: !selected });
			showSnackbar(
				t(selected ? "jobNotifications.bedside.toast.on" : "jobNotifications.bedside.toast.off", { job: jobLabel(facility, item.label) }),
				"success",
			);
		} catch {
			// The hook reverts and tracks it. The inline error below the chips is the feedback.
		}
	};

	const saveErrorText = (facility: MyFacilityJobNotifications) => {
		const saveError = saveErrors.find((error) => error.nurseId === facility.nurseId);
		if (!saveError) return undefined;
		// The backend's message names no job, so it still shows after the removed job's chip drops off.
		if (saveError.messageKey) return t(saveError.messageKey);
		const failedJobName = facility.jobs.find((job) => job.jobId === saveError.jobId)?.displayName;
		return failedJobName && t("jobNotifications.bedside.saveFailed", { job: failedJobName });
	};

	return (
		// The page is as wide as its widest content, so this section must not set the width. Its text wraps instead.
		<Box data-testid="which-shifts-section" sx={{ mt: 2, contain: "inline-size" }}>
			<EyebrowLabel component="h2" sx={{ px: 3, pb: 0.75 }}>
				{t("jobNotifications.bedside.whichShifts")}
			</EyebrowLabel>
			{isError ? (
				<Box sx={{ ...cardSx, px: 1.5, py: 1.5, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
					<Typography role="alert" sx={{ fontSize: FONT_SIZE.SECONDARY, color: "error.dark" }}>
						{t("jobNotifications.bedside.loadFailed")}
					</Typography>
					<SystemButton
						buttonVariant="borderless"
						size="compact"
						noFullWidthMobile
						loading={isRefetching}
						onClick={() => refetch()}
						data-testid="which-shifts-retry"
					>
						{t("retry")}
					</SystemButton>
				</Box>
			) : (
				<>
					{isMultiFacility && (
						<Typography data-testid="which-shifts-per-facility" sx={{ fontSize: FONT_SIZE.CAPTION, lineHeight: 1.45, color: INK.MUTED, px: 2.5, pb: 0.5 }}>
							{t("jobNotifications.bedside.perFacility")}
						</Typography>
					)}
					{facilityList.map((facility) => (
						<FacilityJobChips
							key={facility.nurseId}
							facility={facility}
							facilityName={isMultiFacility ? facilityName(facility) : undefined}
							cardSx={cardSx}
							saveErrorText={saveErrorText(facility)}
							onToggle={(item, selected) => handleChange(facility, item, selected)}
						/>
					))}
				</>
			)}
		</Box>
	);
};
